import React, { FC, useState } from 'react';
import { Group, Subgroup } from '@app/core/models';
import {
  Box,
  Button,
  Dialog,
  DialogProps,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { ConfirmationModal } from '../ConfirmationModal';
import { LocalSelectV2 } from '../LocalSelectV2/LocalSelectV2';
import { hideNumberControls } from '@app/constants/sx';
import { ImagePickerV2 } from '../ImagePickerV2/ImagePickerV2';
import {
  useCreateSubgroupMutation,
  useDeleteSubgroupMutation,
  useUpdateSubgroupMutation,
} from '@app/core/store/subgroups';
import { useGetGroupsListQuery } from '@app/core/store/groups';
import { omitEmpty } from '@app/utils/omitEmpty';

const validationSchema = yup.object().shape({
  name: yup.string().required('Введите название'),
  group: yup.string().required('Укажите группу'),
  position: yup.string().required('Укажите позицию'),
});

interface FormikData {
  name: string;
  icon: string | null;
  position: string;
  group: string;
}

interface SubgroupDialogProps extends DialogProps {
  subgroup?: Subgroup;
  onSave?: () => void;
}

export const SubgroupDialog: FC<SubgroupDialogProps> = ({
  subgroup,
  onClose,
  onSave,
  ...dialogProps
}) => {
  const [confirm, setConfirm] = useState(false);
  const [preview, setPreview] = useState(subgroup?.icon || '');

  const [updateGroup] = useUpdateSubgroupMutation();
  const [createGroup] = useCreateSubgroupMutation();
  const [deleteGroup] = useDeleteSubgroupMutation();

  const groupsApi = useGetGroupsListQuery({ size: 9999 });
  const groups = groupsApi.data?.data.results ?? [];

  const submitApi = async (values: FormikData, id?: Group['id']) => {
    const preparedData = omitEmpty(values, 'position');
    if (id) {
      try {
        await updateGroup({ ...preparedData, id }).unwrap();
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    } else {
      try {
        await createGroup(preparedData).unwrap();
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    }
  };

  const handleFormikSubmit = async (values: FormikData) => {
    const isSubmitted = await submitApi(values, subgroup?.id);
    if (isSubmitted) {
      onSave?.();
      onClose?.({}, 'backdropClick');
    }
  };

  const { values, setFieldValue, handleChange, handleSubmit, dirty, isValid } =
    useFormik<FormikData>({
      validationSchema,
      onSubmit: handleFormikSubmit,
      initialValues: {
        name: subgroup?.name ?? '',
        icon: subgroup?.icon || null,
        position: subgroup?.position?.toString() ?? '',
        group: subgroup?.groupId.toString() ?? '',
      },
    });

  const handleAddImage = async (files: File[]) => {
    if (!files.length) return;
    const [file] = files;
    setFieldValue('icon', file);
    const url = URL.createObjectURL(file);
    setPreview(url);
  };

  const handleCancelClick = () => {
    onClose?.({}, 'backdropClick');
  };

  const handleAcceptClick = () => {
    handleSubmit();
  };

  const handleDeleteClick = () => {
    setConfirm(true);
  };

  const handleConfirm = async () => {
    if (!subgroup) return;
    try {
      await deleteGroup({ id: subgroup.id }).unwrap();
      onClose?.({}, 'backdropClick');
    } catch (error) {
      console.error(error);
      setConfirm(false);
    }
  };

  return (
    <Dialog
      onClose={onClose}
      PaperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          padding: '20px',
          alignItems: 'flex-start',
          gap: '16px',
        },
      }}
      {...dialogProps}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          margin: '0 0 16px 0',
          width: '100%',
        }}
      >
        <Typography sx={{ fontSize: '30px' }}>
          {subgroup ? subgroup.name : 'Новая подгруппа'}
        </Typography>
        {subgroup ? (
          <Button color="error" onClick={handleDeleteClick}>
            Удалить
          </Button>
        ) : null}
      </Box>
      <ImagePickerV2
        onDrop={handleAddImage}
        imagePreview={preview}
        label="Иконка"
        sx={{ width: '100%' }}
      />
      <TextField
        label="Название *"
        name="name"
        value={values.name}
        onChange={handleChange}
        fullWidth
      />
      <LocalSelectV2
        label="Группа *"
        value={values.group}
        onChange={handleChange}
        inputProps={{ name: 'group' }}
        sx={{ width: '100%' }}
      >
        {groups.map(g => (
          <MenuItem key={g.id} value={g.id.toString()}>
            {g.name}
          </MenuItem>
        ))}
      </LocalSelectV2>
      <TextField
        label="Позиция *"
        name="position"
        type="number"
        sx={hideNumberControls}
        value={values.position}
        onChange={handleChange}
        fullWidth
      />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          margin: '16px 0 0 0',
          width: '100%',
        }}
      >
        <Button variant="contained" onClick={handleCancelClick}>
          {subgroup ? 'Закрыть без изменений' : 'Отмена'}
        </Button>
        <Button
          onClick={handleAcceptClick}
          disabled={subgroup ? !dirty || !isValid : !isValid}
        >
          {subgroup ? 'Сохранить изменения' : 'Создать'}
        </Button>
      </Box>
      <ConfirmationModal
        open={confirm}
        onConfirm={handleConfirm}
        onClose={() => setConfirm(false)}
      />
    </Dialog>
  );
};
